/**
 * CFA Note: This is copied and modified from: https://github.com/bvaughn/react-virtualized/blob/master/source/CellMeasurer/CellMeasurerCache.js
 * The problem is that the existing cache is super slow once a lot of rows have loaded.
 * This is due to a slow algorithm used in _updateCachedColumnAndRowSizes.
 * See discussion in this issue: https://github.com/bvaughn/react-virtualized/issues/1747
 * 
 * We've modified this to use a much faster algorithm since we can guarantee that rows are never removed,
 * and that cells never decrease in size.
 */

import { CellMeasurerCacheInterface } from "react-virtualized/dist/es/CellMeasurer";

export const DEFAULT_HEIGHT = 30;
export const DEFAULT_WIDTH = 100;

// Enables more intelligent mapping of a given column and row index to an item ID.
// This prevents a cell cache from being invalidated when its parent collection is modified.
type KeyMapper = (rowIndex: number, columnIndex: number) => string;

type CellMeasurerCacheParams = {
    defaultHeight?: number,
    defaultWidth?: number,
    fixedHeight?: boolean,
    fixedWidth?: boolean,
    minHeight?: number,
    minWidth?: number,
    keyMapper?: KeyMapper,
};

type Cache = {
    [key: string | number]: number,
};

type IndexParam = {
    index: number,
};

/**
 * Caches measurements for a given cell.
 */
export default class CFACellMeasurerCache implements CellMeasurerCacheInterface {
    _cellHeightCache: Cache = {};
    _cellWidthCache: Cache = {};
    _columnWidthCache: Cache = {};
    _rowHeightCache: Cache = {};
    _defaultHeight: number;
    _defaultWidth: number;
    _minHeight: number;
    _minWidth: number;
    _keyMapper: KeyMapper;
    _hasFixedHeight: boolean;
    _hasFixedWidth: boolean;
    _columnCount = 0;
    _rowCount = 0;

    constructor({
        defaultHeight,
        defaultWidth,
        fixedHeight,
        fixedWidth,
        keyMapper,
        minHeight,
        minWidth,
    }: CellMeasurerCacheParams = {}) {
        this._hasFixedHeight = fixedHeight === true;
        this._hasFixedWidth = fixedWidth === true;
        this._minHeight = minHeight || 0;
        this._minWidth = minWidth || 0;
        this._keyMapper = keyMapper || defaultKeyMapper;

        this._defaultHeight = Math.max(
            this._minHeight,
            typeof defaultHeight === 'number' ? defaultHeight : DEFAULT_HEIGHT,
        );
        this._defaultWidth = Math.max(
            this._minWidth,
            typeof defaultWidth === 'number' ? defaultWidth : DEFAULT_WIDTH,
        );

        if (process.env.NODE_ENV !== 'production') {
            if (this._hasFixedHeight === false && this._hasFixedWidth === false) {
                console.warn(
                    "CellMeasurerCache should only measure a cell's width or height. " +
                    'You have configured CellMeasurerCache to measure both. ' +
                    'This will result in poor performance.',
                );
            }

            if (this._hasFixedHeight === false && this._defaultHeight === 0) {
                console.warn(
                    'Fixed height CellMeasurerCache should specify a :defaultHeight greater than 0. ' +
                    'Failing to do so will lead to unnecessary layout and poor performance.',
                );
            }

            if (this._hasFixedWidth === false && this._defaultWidth === 0) {
                console.warn(
                    'Fixed width CellMeasurerCache should specify a :defaultWidth greater than 0. ' +
                    'Failing to do so will lead to unnecessary layout and poor performance.',
                );
            }
        }
    }

    /**
     * CFA Modified - We don't expect to ever need this, commenting out the call to update the cached sizes.
     */
    clear(rowIndex: number, columnIndex: number = 0) {
        console.warn(`Didn't expect clear to be called`);

        const key = this._keyMapper(rowIndex, columnIndex);

        delete this._cellHeightCache[key];
        delete this._cellWidthCache[key];

        // this._updateCachedColumnAndRowSizes(rowIndex, columnIndex);
    }

    clearAll() {
        this._cellHeightCache = {};
        this._cellWidthCache = {};
        this._columnWidthCache = {};
        this._rowHeightCache = {};
        this._rowCount = 0;
        this._columnCount = 0;
    }

    columnWidth = ({ index }: IndexParam) => {
        const key = this._keyMapper(0, index);

        return this._columnWidthCache[key] !== undefined
            ? this._columnWidthCache[key]
            : this._defaultWidth;
    };

    get defaultHeight(): number {
        return this._defaultHeight;
    }

    get defaultWidth(): number {
        return this._defaultWidth;
    }

    hasFixedHeight(): boolean {
        return this._hasFixedHeight;
    }

    hasFixedWidth(): boolean {
        return this._hasFixedWidth;
    }

    getHeight(rowIndex: number, columnIndex: number = 0): number {
        if (this._hasFixedHeight) {
            return this._defaultHeight;
        } else {
            const key = this._keyMapper(rowIndex, columnIndex);

            return this._cellHeightCache[key] !== undefined
                ? Math.max(this._minHeight, this._cellHeightCache[key])
                : this._defaultHeight;
        }
    }

    getWidth(rowIndex: number, columnIndex: number = 0): number {
        if (this._hasFixedWidth) {
            return this._defaultWidth;
        } else {
            const key = this._keyMapper(rowIndex, columnIndex);

            return this._cellWidthCache[key] !== undefined
                ? Math.max(this._minWidth, this._cellWidthCache[key])
                : this._defaultWidth;
        }
    }

    has(rowIndex: number, columnIndex: number = 0): boolean {
        const key = this._keyMapper(rowIndex, columnIndex);

        return this._cellHeightCache[key] !== undefined;
    }

    rowHeight = ({ index }: IndexParam) => {
        const key = this._keyMapper(index, 0);

        return this._rowHeightCache[key] !== undefined
            ? this._rowHeightCache[key]
            : this._defaultHeight;
    };

    set(
        rowIndex: number,
        columnIndex: number,
        width: number,
        height: number,
    ): void {
        const key = this._keyMapper(rowIndex, columnIndex);

        if (columnIndex >= this._columnCount) {
            this._columnCount = columnIndex + 1;
        }
        if (rowIndex >= this._rowCount) {
            this._rowCount = rowIndex + 1;
        }

        // Size is cached per cell so we don't have to re-measure if cells are re-ordered.
        this._cellHeightCache[key] = height;
        this._cellWidthCache[key] = width;

        this._updateCachedColumnAndRowSizes(rowIndex, columnIndex, width, height);
    }

    /**
     * CFA Modified - just record the max for width/height.
     * We know that our cells never get removed or change size.
     */
    _updateCachedColumnAndRowSizes(rowIndex: number, columnIndex: number, width: number, height: number) {
        if (!this._hasFixedWidth) {
            const existingMax = this._columnWidthCache[columnIndex] || 0;
            this._columnWidthCache[columnIndex] = Math.max(existingMax, width);
        }
        if (!this._hasFixedHeight) {
            const existingMax = this._rowHeightCache[rowIndex] || 0;
            this._rowHeightCache[rowIndex] = Math.max(existingMax, height);
        }
    }
}

function defaultKeyMapper(rowIndex: number, columnIndex: number) {
    return `${rowIndex}-${columnIndex}`;
}